<template>
  <div class="offer-product-picker">
    <div class="picker-toolbar d-flex flex-wrap align-items-center gap-2 mb-2">
      <div class="search-wrapper flex-grow-1">
        <i class="fas fa-search search-icon"></i>
        <input
          v-model="search"
          type="text"
          class="form-control form-control-sm ps-4"
          placeholder="Buscar producto..."
        />
      </div>
      <span class="text-muted small">{{ modelValue.length }} seleccionado(s)</span>
    </div>

    <div v-if="selectedProducts.length" class="selected-chips mb-2">
      <span
        v-for="product in selectedProducts"
        :key="product.id"
        class="selected-chip"
      >
        {{ product.name }}
        <button
          type="button"
          class="chip-remove"
          :aria-label="`Quitar ${product.name}`"
          @click="toggleProduct(product.id, false)"
        >
          <i class="fas fa-times"></i>
        </button>
      </span>
    </div>

    <div class="picker-list panel-card">
      <div class="picker-list-header d-flex align-items-center gap-2 px-2 py-2 border-bottom border-secondary-subtle">
        <div class="form-check mb-0">
          <input
            ref="selectAllRef"
            :id="`${inputId}-all`"
            class="form-check-input"
            type="checkbox"
            :checked="allVisibleSelected"
            @change="toggleAllVisible"
          />
          <label class="form-check-label small" :for="`${inputId}-all`">
            {{ allVisibleSelected ? 'Quitar visibles' : 'Seleccionar visibles' }}
          </label>
        </div>
        <button
          v-if="modelValue.length"
          type="button"
          class="btn btn-link btn-sm text-muted ms-auto p-0"
          @click="clearAll"
        >
          Limpiar todo
        </button>
      </div>

      <div class="picker-scroll">
        <label
          v-for="product in filteredProducts"
          :key="product.id"
          class="picker-row"
          :class="{ 'picker-row--selected': isSelected(product.id) }"
        >
          <input
            class="form-check-input mt-0"
            type="checkbox"
            :checked="isSelected(product.id)"
            @change="toggleProduct(product.id, $event.target.checked)"
          />
          <ProductImage :product="product" :alt="product.name" class="picker-thumb" />
          <span class="picker-row-body">
            <span class="picker-name">{{ product.name }}</span>
            <span class="picker-meta text-muted">
              {{ typeLabel(product.type) }}
              <template v-if="product.presentations">
                · {{ product.presentations }} presentación{{ product.presentations === 1 ? '' : 'es' }} en tienda
              </template>
            </span>
          </span>
        </label>

        <p v-if="!products.length" class="text-muted text-center py-4 mb-0 small">
          No hay productos disponibles. Publicá productos en Tienda ingreso primero.
        </p>
        <p v-else-if="!filteredProducts.length" class="text-muted text-center py-4 mb-0 small">
          Ningún producto coincide con la búsqueda.
        </p>
      </div>
    </div>

    <small v-if="hint" class="text-muted d-block mt-2">{{ hint }}</small>
  </div>
</template>

<script setup>
import ProductImage from '@/components/ProductImage.vue'
import { computed, ref, watch } from 'vue'

const props = defineProps({
  modelValue: { type: Array, default: () => [] },
  products: { type: Array, default: () => [] },
  inputId: { type: String, default: 'offer-products' },
  hint: { type: String, default: 'Las ofertas aplican a productos publicados en la tienda ingreso.' },
})

const emit = defineEmits(['update:modelValue'])

const search = ref('')
const selectAllRef = ref(null)

const typeLabel = (t) =>
  ({ fem: 'Fotoperiódica', auto: 'Autofloreciente', merch: 'Merchandising', custom: 'Exclusivo' }[t] || t || '—')

const normalizedIds = computed(() =>
  (props.modelValue || []).map((id) => Number(id)).filter((id) => !Number.isNaN(id))
)

const filteredProducts = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return props.products
  return props.products.filter((p) => (p.name || '').toLowerCase().includes(q))
})

const selectedProducts = computed(() =>
  props.products.filter((p) => normalizedIds.value.includes(Number(p.id)))
)

const isSelected = (id) => normalizedIds.value.includes(Number(id))

const allVisibleSelected = computed(() => {
  if (!filteredProducts.value.length) return false
  return filteredProducts.value.every((p) => isSelected(p.id))
})

const someVisibleSelected = computed(() =>
  filteredProducts.value.some((p) => isSelected(p.id)) && !allVisibleSelected.value
)

watch([someVisibleSelected, allVisibleSelected], () => {
  if (selectAllRef.value) {
    selectAllRef.value.indeterminate = someVisibleSelected.value
  }
}, { immediate: true })

const toggleProduct = (id, checked) => {
  const numId = Number(id)
  let next = [...normalizedIds.value]
  if (checked) {
    if (!next.includes(numId)) next.push(numId)
  } else {
    next = next.filter((v) => v !== numId)
  }
  emit('update:modelValue', next)
}

const toggleAllVisible = () => {
  const visibleIds = filteredProducts.value.map((p) => Number(p.id))
  if (allVisibleSelected.value) {
    emit(
      'update:modelValue',
      normalizedIds.value.filter((id) => !visibleIds.includes(id))
    )
    return
  }
  const merged = new Set([...normalizedIds.value, ...visibleIds])
  emit('update:modelValue', [...merged])
}

const clearAll = () => emit('update:modelValue', [])
</script>

<style scoped>
.offer-product-picker {
  width: 100%;
}

.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  left: 0.55rem;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.75rem;
  pointer-events: none;
}

.selected-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.selected-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: rgba(var(--bs-primary-rgb), 0.18);
  border: 1px solid rgba(var(--bs-primary-rgb), 0.35);
  color: var(--white-color);
  font-size: 0.78rem;
}

.chip-remove {
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.7);
  padding: 0;
  line-height: 1;
  cursor: pointer;
}

.chip-remove:hover {
  color: var(--white-color);
}

.picker-list {
  border: 1px solid var(--panel-border);
  border-radius: 12px;
  overflow: hidden;
}

.picker-scroll {
  max-height: 280px;
  overflow-y: auto;
}

.picker-row {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.75rem;
  margin: 0;
  cursor: pointer;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.picker-row:last-child {
  border-bottom: none;
}

.picker-row:hover {
  background: rgba(255, 255, 255, 0.04);
}

.picker-row--selected {
  background: rgba(var(--bs-primary-rgb), 0.08);
}

.picker-thumb {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
}

.picker-thumb--ph {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.25);
  color: rgba(255, 255, 255, 0.35);
}

.picker-row-body {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.picker-name {
  font-size: 0.88rem;
  color: var(--white-color);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.picker-meta {
  font-size: 0.72rem;
}
</style>
